import { ActionStore } from '@stores'
import loadYNF from './utils/loadYNF'
import loadScript from './utils/loadScript'
import { err } from './utils/report'
import { isType } from './utils/type'
import * as mobx from 'mobx'
import IconLoader from './IconLoader'
import Activity from './Activity'

const isPremises = window.__SERVER_ENV__ === 'premises'

type metaNode = {
  controlType: string,
  alias: string,
  caption: string,
  libCode: string,
  params?: object,
  children?: Array<metaNode>
  validator: string,
}
export default class RootStore extends Activity {
  public actions: any;
  public dataSource: any;
  public reactions: any;
  public history: any;
  private pendingReactionExtendQueue: Array<any>

  constructor (props: any) {
    super(props)
    const { meta } = props
    this.reactions = {}
    this.actions = {}
    this.dataSource = {}
    this.pendingReactionExtendQueue = []
    this.init(meta)
  }

  async init (meta: any) {
    this.parseDataSource(meta)
    this.runIconLoader();

    /**
     * rootStore 解析优先级：
     * - 解析和挂载 context、pageStore、dataSource、utils、api、ErrorHandle 等基础公共能力
     * - 解析和挂载 Stores 实例【含默认生成和扩展】
     * - 解析和挂载 Actions 实例【含默认生成和扩展】
     * - 解析和挂载 Reactions 实例【含默认生成和扩展】
     */

    /**
     * 扩展脚本执行优先级：
     * - domain：领域扩展，及多个领域（通过二方包流水线发布扩展脚本，MF规范）
     * - industry：行业扩展，及多个行业（通过二方包流水线发布扩展脚本，MF规范）
     * - isv：ISV 生态扩展
     * - tenant：租户扩展、项目扩展（通过设计器编写和发布扩展脚本，AMD规范）
     */
    await this.parseStores(meta)
    await this.parseActions(meta)
    await this.runExtendScript()
    await this.runDesignerExtend(meta)
    await this.parseFuncLibs(meta)
    await this.parseReactions(meta)
    await this.runReactionExtend()
    await this.onBeforeRender?.(meta)
    this.ready = true

    this.reactionError()
  }

  /**
   * 监听处理全局 Reaction 异常
   */
  reactionError () {
    mobx.onReactionError((e) => {
      console.error('------onReactionError-------', e)
    });
  }
  runIconLoader () {
      IconLoader(this.isMobile).then(IconManager => {
        window.IconManager = IconManager;
      }).catch(e => {
        console.error("IconManager加载失败", e)
      })
  }

  async runDesignerExtend (meta) {
    // 专属化取文件流，公有云在cdn存在的情况下取cdn否则取文件流
    const getScriptURL = ([propStream, propCDN]) => {
      return isPremises ? meta[propStream] : (meta[propCDN] || meta[propStream])
    }
    // 默认生成（交互规则）
    await this.runDesignerScript(getScriptURL(['computedAttrUrl', 'computedAttrCdn']))
    // 用户自定义（脚本编辑器）
    await this.runDesignerScript(getScriptURL(['funcFrontUrl', 'funcFrontCdn']))
  }

  async getExtendScript () {
    const { billNo, domainKey } = this.pageStore?.params || {}
    if (domainKey === 'developplatform') return
    if (!billNo || !domainKey) return
    try {
      const YNFBundle = await loadYNF(`ynf-${domainKey}/${billNo}`, { errReport: true })
      if (!YNFBundle) {
        // throw new Error('Bill oriented extend not exist')
        console.warn('Bill oriented extend not exist')
      }
      return YNFBundle;
    } catch (err) {}
  }

  async runExtendScript () {
    const YNFBundle = await this.getExtendScript()
    if (YNFBundle) {
      await this.runExtend(YNFBundle)
    }
  }

  async runExtend (extend: any = {}, type?: string) {
    const { billNo, domainKey } = this.pageStore.params || {};

    const { ExtendStore, ExtendAction, ExtendRootStore, ExtendReaction } = extend
    const extendType = type === 'DESIGNER' ? '脚本' : '扩展脚本'
    try {
      const extendedStores = await ExtendStore?.(this)
      for (const k in extendedStores) {
        this[k] = extendedStores[k]
      }
    } catch (error) {
      console.error(`${extendType} -> ExtendStore 执行出错`, error)
    }
    if (ExtendReaction) {
      this.pendingReactionExtendQueue.push([ExtendReaction, type])
    }
    try {
      const actionExtend = ExtendAction?.(this)
      const { actions = {} } = this
      // 1.设置扩展
      Object.entries(actions).forEach(([key, actionStore = {}]) => {
        actionStore?.mixin?.(
          (actionExtend?.default ?? actionExtend?.common),
          actionExtend?.[key]
        )
      })
      // 2.通过扩展新建实例
      if (actionExtend) {
        Object.keys(actionExtend).filter(key => !actions[key]).forEach((key = '') => {
          const options = actionExtend[key];
          this.actions[key] = new ActionStore(options, { name: key, libCode: `ynf-${domainKey}/${billNo}`, controlType: key, rootStore: this });
        })
      }
    } catch (error) {
      console.error(`${extendType} -> ExtendAction 执行出错`, error)
    }
    try {
      await ExtendRootStore?.(this)
    } catch (error) {
      console.error(`${extendType} -> ExtendRootStore 执行出错`, error)
    }
  }

  async runReactionExtend () {
    while (this.pendingReactionExtendQueue.length) {
      const [ExtendReaction, type] = this.pendingReactionExtendQueue.shift()
      const extendType = type === 'DESIGNER' ? '脚本' : '扩展脚本'
      try {
        const reactionExtend = await ExtendReaction?.(this)
        const { reactions = {} } = this
        // 1.设置扩展
        Object.entries(reactions).forEach(([key, reaction = {}]) => {
          reaction?.mixin?.(
            (reactionExtend?.default ?? reactionExtend?.common),
            reactionExtend?.[key]
          )
        })
        // 2.通过扩展新建实例
        if (reactionExtend) {
          Object.keys(reactionExtend).filter(key => !reactions[key]).forEach((key = '') => {
            this.reactions[key] = reactionExtend[key]
          })
        }
      } catch (error) {
        console.error(`${extendType} -> ExtendReaction 执行出错`, error)
      }
    }
  }

  parseDataSource (meta: { children: any[] }) {
    const dataSourceList = meta.children.find((child: any) => child.controlType === 'datasourceList')
    if (dataSourceList?.children?.length) {
      for (const dataSource of dataSourceList.children) {
        this.dataSource[dataSource.alias] = dataSource
      }
    }
  }

  async runDesignerScript (scriptURL: string) {
    if (!scriptURL) return
    try {
      const designerFunction = await loadScript(scriptURL, { loader: 'AMD' })
      const res = await designerFunction?.({
        rootStore: this, tools: { mobx, loadYNF }
      })
      if (
        typeof res === 'object' &&
        ['ExtendStore', 'ExtendAction', 'ExtendReaction', 'ExtendRootStore'].some(v => v in res)
      ) {
        await this.runExtend(res, 'DESIGNER')
      }
    } catch (error) {
      this.utils.message.danger(error.message)
    }
  }

  async parseActions (meta: { children: any[] }) {
    await this.batchParse(
      meta.children.find((child: metaNode) => child.controlType === 'Actions'),
      (actionMeta: metaNode, doAction: any, bundle: any) => {
        const { alias, params, caption, controlType, libCode, validator } = actionMeta
        const options = {
          name: alias,
          rootStore: this,
          caption,
          params,
          libCode,
          controlType,
          validator
        }
        let hooks: any;
        Object.entries(actionMeta).forEach(([key, value]) => {
          if (/Hook$/.test(key)) {
            if (!hooks) hooks = {};
            Object.assign(hooks, { [key]: value });
          }
        })
        if (isType(doAction, 'Function')) {
          this.actions[alias] = new ActionStore({ doAction }, options, hooks)
        } else if (isType(doAction, 'Object')) {
          this.actions[alias] = new ActionStore(doAction, options, hooks)
        } else {
          this.actions[alias] = new ActionStore(options, hooks)
        }
      }
    )
  }

  async parseReactions (meta: { children: any[] }) {
    await this.batchParse(
      meta.children.find((child: metaNode) => child.controlType === 'Reactions'),
      (reactionMeta: any, reaction: any) => {
        if (isType(reaction, 'Function')) {
          const res = reaction(this, reactionMeta);
          if (isType(res, 'Object')) {
            Object.assign(this.reactions, res);
          }
        } else {
          Object.entries(reaction).forEach(([key = '', reactionItem]) => {
            try {
              Object.assign(this.reactions, { [key]: reactionItem(this, reactionMeta) });
            } catch (err) {
              Object.assign(this.reactions, { [key]: reactionItem });
            }
          })
        }
      }
    )
  }

  async parseStores (meta: { children: any[] }) {
    await this.batchParse(
      meta.children.find((child: any) => child?.controlType === 'Stores'),
      (storeMeta: metaNode, StoreConstructor: any) => {
        const { alias } = storeMeta
        this[alias] = new StoreConstructor(storeMeta, this)
      }
    )
  }

  async batchParse (batchRoot: metaNode, childCallback: Function) {
    const children = batchRoot?.children
    const batchParsePromises: Array<Promise<any>> = []
    if (children?.length) {
      children.forEach(async (child: any) => {
        const { libCode, controlType } = child
        if (!libCode) return
        const loadYNFPromise = loadYNF(libCode)
        batchParsePromises.push(loadYNFPromise)
        const bundle = await loadYNFPromise
        if (!bundle) return
        const impl = bundle[controlType]
        if (!impl) return err(`${libCode} 未找到 ${controlType}`)
        childCallback(child, impl)
      })
    }
    await Promise.all(batchParsePromises)
  }

  async parseFuncLibs (meta: { children: any[] }) {
    const funcLibs = meta.children.find((child: any) => child?.controlType === 'FunctionLib')
    funcLibs?.children?.forEach(async (func: any) => {
      if (/^ynf-/.test(func.libCode)) {
        const [packageName, funcName] = func.libCode.split('.')
        const bundle = await loadYNF(packageName)
        const implement = funcName ? bundle[funcName] : bundle
        await implement?.(this, func)
      }
    })
  }
}
